import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { CodeLoginForm } from "../components/auth/CodeLoginForm";
import { Loader2 } from "lucide-react";
import { useI18n } from "../i18n";
import { useAuth } from "../context/AuthContext";
import { LangSwitch } from "../components/common/LangSwitch";
import { Logo } from "../components/common/Logo";

const BRANDS = [
  { name: "Tork", src: "/brands/tork.jpg" },
  { name: "Kimberly-Clark", src: "/brands/kimberly-clark.svg" },
  { name: "Vileda", src: "/brands/vileda.svg" },
  { name: "Diversey", cls: "font-bold tracking-tight text-[#00539B]" },
  { name: "Duni", cls: "font-black italic tracking-tight text-[#1D1D1B]" },
];

const BrandLogos = () => (
  <div className="mt-8 grid grid-cols-3 gap-2 sm:grid-cols-5" data-testid="login-brands">
    {BRANDS.map((b) => (
      <div key={b.name} className="grid h-12 place-items-center rounded-xl bg-white px-2" data-testid={`login-brand-${b.name.toLowerCase()}`}>
        {b.src ? <img src={b.src} alt={b.name} className="max-h-7 max-w-full object-contain" loading="lazy" /> : <span className={`text-lg leading-none ${b.cls}`}>{b.name}</span>}
      </div>
    ))}
  </div>
);

const LoginHero = () => {
  const { t } = useI18n();
  return (
    <div className="relative overflow-hidden bg-navy px-6 pb-10 pt-8 text-white lg:flex lg:flex-col lg:justify-between lg:p-12">
      <img src="/catalogue/house-1.jpg" alt="" className="absolute inset-0 h-full w-full object-cover opacity-20" />
      <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/80 to-navy/40" />
      <div className="relative"><Logo className="h-10 sm:h-12" /></div>
      <div className="relative mt-10 max-w-md lg:mt-0">
        <h1 className="text-3xl font-bold leading-tight sm:text-4xl lg:text-5xl">{t("login.heroTitle")}</h1>
        <p className="mt-6 text-xs font-semibold uppercase tracking-[0.3em] text-brand" data-testid="login-tagline">Premium Business Solutions</p>
        <BrandLogos />
      </div>
    </div>
  );
};

const Field = ({ id, label, ...props }) => (
  <label htmlFor={id} className="block">
    <span className="text-sm font-medium text-navy">{label}</span>
    <input id={id} {...props} className="mt-1.5 h-12 w-full rounded-xl border border-slate-200 bg-white px-4 text-base text-navy outline-none transition-shadow focus:border-brand focus:ring-4 focus:ring-brand/15" />
  </label>
);

export default function Login() {
  const { t } = useI18n();
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(() => (sessionStorage.getItem("vavlis.inactive") ? t("cstat.loginInactive") : ""));
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("password");
  const [remember, setRemember] = useState(() => window.innerWidth < 768);
  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email, password, remember);
      sessionStorage.removeItem("vavlis.inactive");
      navigate("/", { replace: true });
    } catch (err) {
      const status = err.response?.status;
      if (["customer_inactive", "user_inactive"].includes(err.response?.data?.detail)) { setError(t("cstat.loginInactive")); return; }
      sessionStorage.removeItem("vavlis.inactive");
      setError(status === 429 ? t("login.locked") : status === 401 ? t("login.invalid") : t("common.error"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-screen bg-canvas lg:grid-cols-2">
      <LoginHero />
      <div className="flex flex-col px-5 py-6 sm:px-10">
        <div className="flex justify-end"><LangSwitch /></div>
        <div data-testid="login-card" className="mx-auto mt-6 w-full max-w-sm rounded-3xl bg-white p-6 shadow-[0_8px_30px_rgba(0,32,104,0.08)] sm:p-8 lg:my-auto">
          <h2 className="text-2xl font-bold text-navy">{t("login.title")}</h2>
          <p className="mt-1 text-sm text-slate-500">{t("login.subtitle")}</p>
          {mode === "code" ? <CodeLoginForm remember={remember} /> : (
          <form onSubmit={submit} data-testid="login-form">
          <div className="mt-6 space-y-4">
            <Field id="email" label={t("login.email")} type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email-input" />
            <Field id="password" label={t("login.password")} type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password-input" />
          </div>
          <div className="mt-2 text-right"><Link to="/forgot-password" className="inline-block py-2 text-sm font-medium text-vblue-ink" data-testid="forgot-password-link">{t("auth.forgotLink")}</Link></div>
          {error && <p role="alert" data-testid="login-error" className="mt-2 rounded-xl bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</p>}
          <button type="submit" disabled={busy} data-testid="login-submit-button" className="mt-4 flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-brand text-base font-semibold text-navy transition-[background-color,transform] duration-200 hover:bg-[#8BC21F] active:scale-[0.98] disabled:opacity-60">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} {t("login.submit")}
          </button>
          </form>
          )}
          <label className="mt-4 flex min-h-11 cursor-pointer items-center gap-3 text-sm text-navy">
            <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="h-5 w-5 accent-[#002068]" data-testid="login-remember-checkbox" />
            {t("auth.remember")}
          </label>
          <button type="button" onClick={() => setMode(mode === "code" ? "password" : "code")} data-testid="login-mode-toggle" className="mt-2 h-11 w-full rounded-xl border border-navy/15 text-sm font-semibold text-navy transition-colors hover:bg-canvas">
            {mode === "code" ? t("auth.usePassword") : t("auth.useCode")}
          </button>
          <p className="mt-5 text-center text-xs text-slate-500">{t("login.noSignup")}</p>
        </div>
      </div>
    </div>
  );
}
